import React from "react";
import { IconButton, Stack } from "@mui/material";
import { Controller } from "react-hook-form";
import { createStyles, FormControlProps, makeStyles, TextField, TextFieldProps, Theme } from "@material-ui/core";
import LaunchIcon from "@mui/icons-material/Launch";
import { ValidationContext } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";

interface ValidationProps {
  /**
   * React hook form property
   */
  register?: any;
  /**
   * React hook form property
   */
  control?: any;
  /**
   * React hook form property
   */
  errors?: any;
  /**
   * React hook form property
   */
  customHelperText?: any;
}
interface ComponentProps<T> extends ValidationProps {
  /**
   * Name of the property/variable that will be managed by this input
   */
  propertyName: string;
  /**
   * Label of this input
   */
  label: string;
  /**
   * Input default value
   */
  defaultValue?: T;
}

export interface InputFieldProps<T> extends ComponentProps<T> {
  /**
   * Type of the input. e.g: number, text, etc
   */
  inputType?: string;
  /**
   * `true` if the input is required
   */
  required?: boolean;
  /**
   * `true` if the input is read only
   */
  readOnly?: boolean;
  /**
   * MUI Texfield props
   */
  internalProps?: TextFieldProps;
  /**
   * Rex expression that the input must satisfy to be valid
   */
  pattern?: RegExp;
  /**
   * The size of the text field.
   */
  size?: "small" | "medium";
  /**
   * Input style variant
   */
  variant?: FormControlProps["variant"];

  /**
   * If `true`, adds a button to the text field
   */
  addButton?: boolean;

  /**
   * Callback fired when the button of the field is clicked.
   *
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
}

export function TextFieldWithButton(props: InputFieldProps<string>) {
  const classes = useStyles();
  const context = React.useContext(ValidationContext);
  const finalProps = context ? { ...props, ...context } : props;

  return (
    <div>
      <Controller
        name={finalProps.propertyName}
        control={finalProps.control}
        rules={{ required: finalProps.required, pattern: finalProps.pattern }}
        defaultValue=""
        render={({ field }) => (
          <>
            <Stack direction={"row"}>
              <TextField
                size={finalProps.size}
                label={finalProps.label}
                {...field}
                {...finalProps.internalProps}
                className={classes.fullWidth}
                disabled={!!finalProps.readOnly}
                type={finalProps.inputType || "text"}
                variant={finalProps.variant ?? "filled"}
                error={!!finalProps.errors[finalProps.propertyName]}
                helperText={
                  finalProps.errors[finalProps.propertyName] && (finalProps.customHelperText || finalProps.label + " should be provided")
                }
              />
              {props.addButton && (
                <>
                  <IconButton onClick={props.onClick}>
                    <LaunchIcon />
                  </IconButton>
                </>
              )}
            </Stack>
          </>
        )}
      />
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: "1000px",
    },
  }),
  { index: 1 },
);
